import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import {
	TestsBySection,
	TestsBySectionVersion,
} from "../models/TestsBySection";

export const getTestsBySectionVersions = async (
	signal?: AbortSignal,
): Promise<TestsBySectionVersion[]> => {
	const apiService = new MESApiService();

	const resp = await apiService.callV2(
		"[LAB].[GetTestsBySectionVersions]",
		[],
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as TestsBySectionVersion[];
	} else {
		throw new Error(resp.message || "Error fetching tests by section versions");
	}
};

export const getTestsBySection = async (
	params: { versionId?: number | null },
	signal?: AbortSignal,
): Promise<TestsBySection[]> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "TestsBySectionVersionId", value: params.versionId ?? null },
	];

	const resp = await apiService.callV2(
		"[LAB].[GetTestsBySection]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as TestsBySection[];
	} else {
		throw new Error(resp.message || "Error fetching tests by section");
	}
};

export const createTestsBySectionVersion = async (params: {
	fromVersionId?: number | null;
	rev: number;
	effectiveDate: Date;
	comments?: string | null;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "FromVersionId", value: params.fromVersionId ?? null },
		{ name: "Rev", value: params.rev },
		{ name: "EffectiveDate", value: params.effectiveDate.toISOString() },
		{ name: "Comments", value: params.comments ?? null },
	];

	const resp = await apiService.callV2(
		"[LAB].[CreateTestsBySectionVersion]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error creating tests by section version");
	}
};

export const publishTestsBySectionVersion = async (params: {
	versionId: number;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "TestsBySectionVersionId", value: params.versionId },
	];

	const resp = await apiService.callV2(
		"[LAB].[PublishTestsBySectionVersion]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(
			resp.message || "Error publishing tests by section version",
		);
	}
};

export const deleteTestsBySectionVersion = async (params: {
	versionId: number;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "TestsBySectionVersionId", value: params.versionId },
	];

	const resp = await apiService.callV2(
		"[LAB].[DeleteTestsBySectionVersion]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(
			resp.message || "Error discarding tests by section version",
		);
	}
};

export const upsertTestsBySection = async (params: {
	testsBySection: TestsBySection;
	versionId: number;
}) => {
	const apiService = new MESApiService();
	const t = params.testsBySection;

	const parameters: spParameter[] = [
		{ name: "TestsBySectionId", value: t.TestsBySectionId ?? null },
		{ name: "SectionId", value: t.SectionId },
		{ name: "TestsBySectionVersionId", value: params.versionId },
		{ name: "GradeId", value: t.GradeId ?? null },
		{ name: "Pull2", value: t.Pull2 },
		{ name: "Pull8", value: t.Pull8 },
		{ name: "Pull505", value: t.Pull505 },
		{ name: "Impact", value: t.Impact },
		{ name: "Bend", value: t.Bend },
		{ name: "Hardness", value: t.Hardness },
		{ name: "Comments", value: t.Comments },
	];

	const resp = await apiService.callV2(
		"[LAB].[UpsertTestsBySection]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error saving tests by section");
	}
};

export const deleteTestsBySection = async (params: {
	testsBySectionId: number;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "TestsBySectionId", value: params.testsBySectionId },
	];

	const resp = await apiService.callV2(
		"[LAB].[DeleteTestsBySection]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting tests by section");
	}
};
